import { useDispatch, useSelector } from 'react-redux';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import useMetadataUpdate from '../../../../hooks/useMetadataUpdate';
import { useQuery } from 'react-fetching-library';
import getFullName from '../../../../helpers/user';
import { Grid, makeStyles } from '@material-ui/core';
import Loader from '../../../Shared/Loader';
import { getLocalizedDate } from '../../../../helpers/localize';
import { PATIENT_LANGUAGE_BY_KEY } from '../../../Patients/constants';
import { formatPhoneNumber } from '../../../../helpers/phone';
import {
  openPatientPane,
  setCurrentPageData,
  toggleIsInfoPaneExpanded,
} from '../../../../redux_store/reducer/reducers/infoPaneReducer';
import ListIcon from '@material-ui/icons/List';
import PatientEditModal from '../../../Patients/PatientEditModal';
import { PATIENT_TABLE_NAMES, routes } from '../../../../config/routes';
import { AppRouteContext } from '../../../../contexts';
import InfoRow from '../../widgets/InfoRow';
import BaseInfoWidget from '../../widgets/BaseInfoWidget';
import { useParams } from 'react-router-dom';

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
  },
  form: {},
}));

let timeoutID;

const PatientInfo = () => {
  const {
    currentPage: {
      currentPageContext,
      currentPageData: patient,
    },
  } = useSelector((state) => state.infoPane);
  const { accountUuid: paramAccountUuid, patientUuid: paramsPatientUuid } = useParams();
  const [openPatientEditModal, setOPenPatientEditModal] = useState(false);
  const [canShowLoader, setCanShowLoader] = useState(false);
  const { en: { titles, forms, infoPane } } = useSelector((state) => state.i18n);
  const { setAppRoute } = useContext(AppRouteContext);
  const classes = useStyles();
  const dispatch = useDispatch();

  useEffect(() => {
    if (paramAccountUuid && paramsPatientUuid && paramsPatientUuid !== patient?.uuid) {
      const newData = {
        accountUUID: paramAccountUuid,
        patientUUID: paramsPatientUuid,
        patientFirstName: '',
        patientLastName: '',
      };
      dispatch(openPatientPane(newData));
    }
  }, [paramsPatientUuid]);

  const { query, loading: isLoading } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/${currentPageContext.accountUUID}/patients/${currentPageContext.patientUUID ?? currentPageContext.uuid}`,
    },
    false,
  ]);

  const getPatientData = () => {
    query().then(({ error, payload }) => {
      if (!error) {
        dispatch(setCurrentPageData(payload));
      }
    });
  };

  useEffect(() => {
    clearTimeout(timeoutID);
    timeoutID = setTimeout(() => {
      setCanShowLoader(true);
    }, 1500);
  }, []);

  useEffect(() => {
    if (query && !patient) {
      setCanShowLoader(false);
      clearTimeout(timeoutID);
      getPatientData();
      timeoutID = setTimeout(() => {
        setCanShowLoader(true);
      }, 1500);
    }
  }, [currentPageContext]);

  const openPatientPage = () => {
    setAppRoute({
      path: routes.patientDashboard.path,
      params: {
        patientUuid: currentPageContext.patientUUID ?? currentPageContext.uuid,
        accountUuid: currentPageContext.accountUUID,
        tableName: PATIENT_TABLE_NAMES.assessments,
      },
      title: `${titles.patientDashboard}: ${patient.patientFirstName ?? patient.firstName} ${patient.patientLastName ?? patient.lastName}`,
    });
    setTimeout(() => {
      dispatch(toggleIsInfoPaneExpanded(false));
    }, 1000);
  };

  const closePatientEditModal = (wasUpdatedSuccessfully) => {
    setOPenPatientEditModal(false);
    if (wasUpdatedSuccessfully) {
      getPatientData();
    }
  };

  const values = useMemo(
    () => {
      if (!patient) {
        return [];
      }

      const {
        streetAddress,
        suite,
        city,
        state,
        zipCode,
      } = patient.address;

      const address = `${streetAddress ? streetAddress : ''}${streetAddress && suite ? ' ' : ''}${suite ? suite : ''}`;
      const city_state = `${city ? city : ''}${city && state ? ', ' : ''}${state ? state : ''}`;
      const zip = `${zipCode ? zipCode : ''}`;

      const addressValue = [
        ...(address ? [address] : []),
        ...(city_state ? [city_state] : []),
        ...(zip ? [zip] : []),
      ];

      const addressForCopying = address + (address && city_state ? ', ' : '') + city_state + ((address || city_state) && zip ? ' ' : '') + zip;

      return [
        {
          title: forms.mrnNumber,
          info: patient.mrnNumber,
        },
        {
          title: forms.firstName,
          info: patient.firstName,
        },
        {
          title: forms.lastName,
          info: patient.lastName,
        },
        {
          title: forms.phone,
          info: patient?.phone?.phone ? formatPhoneNumber(patient?.phone?.phone) : '-',
        },
        {
          title: forms.email,
          info: patient.email,
          dataActionTooltipTitle: infoPane.sentEmail,
          dataAction: (e) => {
            e.preventDefault();
            e.stopPropagation();

            const recipient = patient.email;
            const subject = encodeURIComponent('');
            const body = encodeURIComponent('');

            // Check if default email client exists (mailto)
            try {
              window.location.href = `mailto:${recipient}?subject=${subject}&body=${body}`;
            } catch (e) {
              // Fallback to Outlook Web App
              window.open(
                `https://outlook.office.com/mail/deeplink/compose?to=${recipient}&subject=${subject}&body=${body}`,
                '_blank',
              );
            }
          },
        },
        {
          title: forms.dateOfBirth,
          info: getLocalizedDate(patient.dateOfBirth, 'UTC'),
        },
        {
          title: forms.genderAtBirth,
          info: forms[patient.genderAtBirth],
        },
        {
          title: forms.drugAllergies,
          info: patient.hasDrugAllergies ? forms.yes : forms.no,
        },
        {
          title: forms.address,
          info: addressValue.length ? addressValue : '',
          customCopyValue: addressForCopying,
        },
        {
          title: forms.patientLanguage,
          info: PATIENT_LANGUAGE_BY_KEY[patient.patientLanguage],
        },
      ];
    },
    [patient],
  );

  return <BaseInfoWidget
    breadcrumbs={infoPane.patient}
    title={getFullName(patient ?? currentPageContext)}
    editButton={{
      hint: infoPane.editPatient,
      onClick: () => setOPenPatientEditModal(true),
      disabled: isLoading || !patient,
    }}
    actions={[
      {
        icon: <ListIcon />,
        tooltipTitle: infoPane.openPatientDashboard,
        buttonTitle: infoPane.openPatientDashboard,
        disabled: isLoading || !patient,
        onClick: openPatientPage,
      },
    ]}
    content={
      <div className={classes.container}>
        {
          isLoading || !patient
            ? canShowLoader ? <Loader fullHeight /> : <div />
            : <div className={classes.form}>
              <Grid container direction={'column'}>
                {values.map((props) => <InfoRow{...props} />)}
              </Grid>
            </div>
        }
        <PatientEditModal
          open={openPatientEditModal}
          patientUuid={currentPageContext.patientUUID ?? currentPageContext.uuid}
          accountUuid={currentPageContext.accountUUID}
          handleClose={closePatientEditModal}
        />
      </div>
    }
  />;
};

export default PatientInfo;
